import React, { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { SettingsDrawer } from "@components-shared/mobile/settings-drawer";
import { Button } from "@components/buttons";
import { EventContext, LookupContext } from "@gp/components";
import EventViewStore from "@state/stores/offer/event/EventViewStore.mobile";
import { observer } from "mobx-react-lite";

import { EventContentSkeleton } from "../EventContentSkeleton";
import { CompetitionAverages } from "./CompetitionAverages";
import { GoalDistribution } from "./GoalDistribution";
import { HeadToHead } from "./HeadToHead";
import { StreakSection } from "./StreakSection";

export const StreakStatsBody = observer(function StreakStatsBody({
	store,
	isSettingsDrawerOpen,
	onClose,
}: {
	store: EventViewStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	if (store.sportStatisticsStore.loader.isLoading) {
		return <EventContentSkeleton />;
	}

	if (!store.sportStatisticsStore.filteredStreakStats) {
		return null;
	}

	return (
		<>
			<GroupedStreakStatsDrawer
				store={store}
				isSettingsDrawerOpen={isSettingsDrawerOpen}
				onClose={onClose}
			/>
			{store.sportStatisticsStore.filteredStreakStats.data
				.competitionAverages && (
				<CompetitionAverages
					title={t(
						"EVENT.STATISTICS.STREAK_STATS.TITLE_COMPETITION_AVERAGES"
					)}
					data={
						store.sportStatisticsStore.filteredStreakStats.data
							.competitionAverages
					}
					tournament={store.tournament}
					homeTeam={store.teamOne}
					awayTeam={store.teamTwo}
				/>
			)}

			{store.sportStatisticsStore.filteredStreakStats.data.headToHead && (
				<HeadToHead
					title={t(
						"EVENT.STATISTICS.STREAK_STATS.TITLE_HEAD_TO_HEAD"
					)}
					data={
						store.sportStatisticsStore.filteredStreakStats.data
							.headToHead
					}
					tournament={store.tournament}
					homeTeam={store.teamOne}
					awayTeam={store.teamTwo}
				/>
			)}

			{store.sportStatisticsStore.filteredStreakStats.data.general && (
				<StreakSection
					title={t("EVENT.STATISTICS.STREAK_STATS.TITLE_STREAKS")}
					data={
						store.sportStatisticsStore.filteredStreakStats.data
							.general
					}
					tournament={store.tournament}
					homeTeam={store.teamOne}
					awayTeam={store.teamTwo}
				/>
			)}

			{store.sportStatisticsStore.filteredStreakStats.data
				.goalDistribution && (
				<GoalDistribution
					title={t("EVENT.STATISTICS.GOAL_DISTRIBUTION.TITLE")}
					data={
						store.sportStatisticsStore.filteredStreakStats.data
							.goalDistribution
					}
					tournament={store.tournament}
					homeTeam={store.teamOne}
					awayTeam={store.teamTwo}
				/>
			)}
		</>
	);
});

function GroupedStreakStatsDrawer(props: {
	store: EventViewStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	const [streakFilter, setStreakFilter] = useState<string>(
		props.store.sportStatisticsStore.streakFilter
	);

	const event = useContext(EventContext);
	const { teams } = useContext(LookupContext);

	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	if (homeTeam == null || awayTeam == null) {
		console.error("Expected teams, got one or more null.");
		return null;
	}
	return (
		<SettingsDrawer
			onClose={() => {
				setStreakFilter(props.store.sportStatisticsStore.streakFilter);
				props.onClose(false);
			}}
			isOpen={props.isSettingsDrawerOpen}
		>
			<div className="drawer__header">
				<div className="drawer__heading">
					{t("EVENT.HEADER.STREAKS")}
				</div>
			</div>

			<div className="drawer__body">
				<div className="drawer__section">
					<div className="drawer__label label u-mar--bottom--reset">
						{t("EVENT.HEADER.FILTER_STATISTICS")}
					</div>

					{props.store.sportStatisticsStore.availableStreakFilterOptions.map(
						(group) => (
							<div
								key={group.key}
								className="drawer__option input--radio"
							>
								<input
									type="radio"
									id={`radio-${group.key}`}
									name="streakTabFilter"
									value={group.key}
									checked={streakFilter === group.key}
									onChange={() => setStreakFilter(group.key)}
									className="input--radio__box"
								/>
								<label
									htmlFor={`radio-${group.key}`}
									className="input--radio__label"
								>
									{t(group.title)}
								</label>
							</div>
						)
					)}
				</div>
			</div>

			<div className="drawer__footer">
				<Button
					btnText={t("ACC_STATEMENT.BET_DETAILS.CLOSE_BTN")}
					onClick={() => {
						setStreakFilter(
							props.store.sportStatisticsStore.streakFilter
						);
						props.onClose(false);
					}}
					className="btn--med btn--block btn--primary--inverted drawer__action--secondary"
				/>

				<Button
					btnText={t("mybets:PAGE.FILTER_FORM.SUBMIT_BUTTON")}
					disabled={
						!(
							streakFilter !==
							props.store.sportStatisticsStore.streakFilter
						)
					}
					onClick={() => {
						if (
							!(
								streakFilter !==
								props.store.sportStatisticsStore.streakFilter
							)
						) {
							return;
						}

						props.store.sportStatisticsStore.setStreakFilter(
							streakFilter
						);
						props.onClose(false);
					}}
					className="btn--med btn--block btn--accent drawer__action--primary"
				/>
			</div>
		</SettingsDrawer>
	);
}
